import { useEffect, useRef } from 'react';
import { PLAYER_SIZE } from '../constants'; // only what the assembler still needs
import { updatePonds, updateRocks, updateTrees, updateChests, updateWorldAssets } from './gameLoop/systems/terrain';
import { updatePlayerMovement, resolvePlayerRockCollisions }     from './gameLoop/systems/player';
import { updateTierTracking, updatePhantomStride, updateTierBossSpawn, initBossTimer } from './gameLoop/systems/tierTracking';
import { spawnEnemies, spawnBoss }                               from './gameLoop/systems/enemySpawn';
import { updateEnemyMovement, resolveEnemyRockCollisions }       from './gameLoop/systems/enemyMovement';
import { updateProjectiles }                                     from './gameLoop/systems/projectiles';
import { checkChestCollection, updateGroundDrops }               from './gameLoop/systems/chestDrops';
import { checkAutoAttack, processMeleeAttack }                   from './gameLoop/systems/meleeAttack';
import { checkLevelUp, processPlayerEnemyCollision }             from './gameLoop/systems/playerHit';
import { expireEffects, checkWorldModifier, updateScore }        from './gameLoop/systems/effects';
import { rollChestItem }                                         from '../data/items';
import CFG from './gameLoop/gameLoopConfig.json';

/**
 * Thin assembler hook — drives the rAF loop and delegates every system
 * section to its own focused module.  No game logic lives here.
 *
 * System modules live in src/hooks/gameLoop/systems/.
 * Numeric constants live in src/hooks/gameLoop/gameLoopConfig.json.
 */
export function useGameLoop({
  gameState,
  stateRef,
  lastSpawnRef,
  startTimeRef,
  setGameState,
  setRenderTick,
  setAttackRenderKey,
  setLevelUpSkills,
  setMagicSelectSpells,
  setWorldModifierChoices,
  setChestLootOptions,
}) {
  const animFrameRef = useRef(null);

  useEffect(() => {
    if (gameState !== 'playing') return;

    let lastTimestamp = null;

    const loop = (timestamp) => {
      const dt = lastTimestamp !== null ? Math.min((timestamp - lastTimestamp) / 1000, 0.1) : 0;
      lastTimestamp = timestamp;

      const s = stateRef.current;
      if (!s.alive) return;

      // ── Tier boss chest pending (deferred from last frame) ───────────────
      if (s.tierBossChestPending) {
        s.tierBossChestPending = false;
        const tbLevel = (s.score ?? 0) / 1000;
        setChestLootOptions([rollChestItem(tbLevel), rollChestItem(tbLevel), rollChestItem(tbLevel)]);
        setGameState('chestopen');
        return;
      }

      // ── Frame-level derived values ────────────────────────────────────────
      const terrainDy = s.tierBossAlive ? 0 : dt * CFG.terrain.scrollSpeed;
      const elapsed   = (timestamp - startTimeRef.current) / 1000;
      const tierIdx   = Math.floor(elapsed / CFG.tierTracking.secondsPerTier);
      const pcx       = s.player.x + PLAYER_SIZE / 2;
      const pcy       = s.player.y + PLAYER_SIZE / 2;

      // ── Systems ───────────────────────────────────────────────────────────
      initBossTimer(s, timestamp);

      if (updatePonds(s, dt, timestamp, terrainDy, setGameState)) return;
      updateRocks(s, timestamp, terrainDy);
      updateTrees(s, timestamp, terrainDy);
      updateWorldAssets(s, timestamp, terrainDy);
      updateChests(s, dt, terrainDy);

      updatePlayerMovement(s, dt, timestamp);
      resolvePlayerRockCollisions(s);

      updateTierTracking(s, timestamp, elapsed, { setGameState, setMagicSelectSpells });
      updatePhantomStride(s, timestamp);
      updateTierBossSpawn(s, timestamp);

      spawnEnemies(s, timestamp, elapsed, tierIdx, lastSpawnRef);
      spawnBoss(s, timestamp, tierIdx);

      if (updateEnemyMovement(s, dt, timestamp, pcx, pcy, setGameState)) return;
      resolveEnemyRockCollisions(s);

      if (updateProjectiles(s, dt, timestamp, pcx, pcy, setGameState)) return;

      if (checkChestCollection(s, setChestLootOptions, setGameState)) return;
      updateGroundDrops(s, dt, terrainDy, pcx, pcy);

      checkAutoAttack(s, timestamp, setAttackRenderKey);
      if (processMeleeAttack(s, timestamp, pcx, pcy, setAttackRenderKey, setChestLootOptions, setGameState)) return;

      checkLevelUp(s);
      if (processPlayerEnemyCollision(s, timestamp, pcx, pcy, setGameState)) return;

      expireEffects(s, timestamp);
      checkWorldModifier(s, setWorldModifierChoices, setGameState);
      updateScore(s, terrainDy);

      setRenderTick(t => t + 1);
      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [gameState]); // eslint-disable-line react-hooks/exhaustive-deps
}
